// components/booking/activities/SimpleDayCalendar.tsx - Simple day-only calendar for activities
// SIMPLIFIED: Activities have unlimited stock - all future dates are available

'use client'

import { useState, useMemo } from 'react'
import { useTranslations } from 'next-intl'
import styles from './SimpleDayCalendar.module.css'

interface SimpleDayCalendarProps {
  activityId: string
  selectedDate: string | null
  onSelectDate: (date: string, priceInfo?: { pricePerPerson: number }) => void
  minDate?: Date
  maxMonths?: number
  // Default price per person if not fetching from API
  defaultPricePerPerson?: number
  // Blocked dates from admin
  blockedDates?: string[]
  // Per-day pricing overrides (date string YYYY-MM-DD → price)
  dailyPricing?: Record<string, number>
}

const WEEKDAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const
const MONTH_KEYS = ['january', 'february', 'march', 'april', 'may', 'june',
                    'july', 'august', 'september', 'october', 'november', 'december'] as const

// NOTE: API_BASE removed - no longer fetching availability

export default function SimpleDayCalendar({
  activityId: _activityId,
  selectedDate,
  onSelectDate,
  minDate = new Date(),
  maxMonths = 6,
  defaultPricePerPerson = 0,
  blockedDates = [],
  dailyPricing = {},
}: SimpleDayCalendarProps) {
  const t = useTranslations('bookingActivity.calendar')
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date()
    return { month: now.getMonth(), year: now.getFullYear() }
  })

  // Generate calendar days for current month view
  // SIMPLIFIED: All future dates are available (unlimited stock)
  // EXCEPT blocked dates set by admin
  const calendarDays = useMemo(() => {
    const { month, year } = currentMonth
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const startPadding = firstDay.getDay()
    const blockedSet = new Set(blockedDates)

    const days: Array<{
      date: Date | null
      dateStr: string
      isToday: boolean
      isPast: boolean
      isAvailable: boolean
    }> = []

    // Padding for days before month starts
    for (let i = 0; i < startPadding; i++) {
      days.push({ date: null, dateStr: '', isToday: false, isPast: false, isAvailable: false })
    }

    // Actual days
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    for (let day = 1; day <= lastDay.getDate(); day++) {
      const date = new Date(year, month, day)
      const dateStr = date.toISOString().split('T')[0]
      const isToday = date.getTime() === today.getTime()
      const isPast = date < today
      const isBlocked = blockedSet.has(dateStr)

      days.push({
        date,
        dateStr,
        isToday,
        isPast,
        // SIMPLIFIED: All future dates are available UNLESS blocked by admin
        isAvailable: !isPast && !isBlocked
      })
    }

    return days
  }, [currentMonth, blockedDates])

  // Navigation limits
  const canGoPrev = useMemo(() => {
    const { month, year } = currentMonth
    const current = new Date(year, month, 1)
    const min = new Date(minDate.getFullYear(), minDate.getMonth(), 1)
    return current > min
  }, [currentMonth, minDate])

  const canGoNext = useMemo(() => {
    const { month, year } = currentMonth
    const maxDate = new Date()
    maxDate.setMonth(maxDate.getMonth() + maxMonths)
    const current = new Date(year, month, 1)
    return current < maxDate
  }, [currentMonth, maxMonths])

  const goToPrevMonth = () => {
    if (!canGoPrev) return
    setCurrentMonth(prev => {
      if (prev.month === 0) {
        return { month: 11, year: prev.year - 1 }
      }
      return { month: prev.month - 1, year: prev.year }
    })
  }

  const goToNextMonth = () => {
    if (!canGoNext) return
    setCurrentMonth(prev => {
      if (prev.month === 11) {
        return { month: 0, year: prev.year + 1 }
      }
      return { month: prev.month + 1, year: prev.year }
    })
  }

  const handleDayClick = (dateStr: string, isAvailable: boolean) => {
    if (!isAvailable) return
    // Use daily pricing override if available, otherwise fall back to default
    const pricePerPerson = dailyPricing[dateStr] ?? defaultPricePerPerson
    onSelectDate(dateStr, { pricePerPerson })
  }

  // NOTE: Error and loading states REMOVED - no API fetching needed
  // Activities have unlimited stock - all future dates are always available

  return (
    <div className={styles.calendar}>
      {/* Header */}
      <div className={styles.header}>
        <button
          className={styles.navButton}
          onClick={goToPrevMonth}
          disabled={!canGoPrev}
          aria-label={t('previousMonth')}
        >
          <ChevronLeftIcon />
        </button>

        <h3 className={styles.monthTitle}>
          {t(`months.${MONTH_KEYS[currentMonth.month]}`)} {currentMonth.year}
        </h3>

        <button
          className={styles.navButton}
          onClick={goToNextMonth}
          disabled={!canGoNext}
          aria-label={t('nextMonth')}
        >
          <ChevronRightIcon />
        </button>
      </div>

      {/* Weekday headers */}
      <div className={styles.weekdays}>
        {WEEKDAY_KEYS.map(key => (
          <div key={key} className={styles.weekday}>{t(`weekdays.${key}`)}</div>
        ))}
      </div>

      {/* Days grid */}
      <div className={styles.daysGrid}>
        {calendarDays.map((day, index) => {
          if (!day.date) {
            return <div key={`empty-${index}`} className={styles.emptyDay} />
          }

          const isSelected = day.dateStr === selectedDate
          // SIMPLIFIED: All future dates are available (unlimited stock)
          const isAvailable = day.isAvailable
          const isBlocked = blockedDates.includes(day.dateStr)

          let statusClass = styles.dayUnavailable
          if (day.isPast) {
            statusClass = styles.dayPast
          } else if (isBlocked) {
            statusClass = styles.dayBlocked
          } else if (isAvailable) {
            statusClass = styles.dayAvailable
          }

          return (
            <button
              key={day.dateStr}
              className={`${styles.day} ${statusClass} ${isSelected ? styles.daySelected : ''} ${day.isToday ? styles.dayToday : ''}`}
              onClick={() => handleDayClick(day.dateStr, isAvailable)}
              disabled={day.isPast}
              aria-label={`${day.date.getDate()} ${t(`months.${MONTH_KEYS[currentMonth.month]}`)} - ${isAvailable ? t('available') : t('pastDate')}`}
            >
              <span className={styles.dayNumber}>{day.date.getDate()}</span>
              {/* Price display removed - shown elsewhere in booking flow */}
            </button>
          )
        })}
      </div>

      {/* Simplified Legend - no limited spots indicator needed */}
      <div className={styles.legend}>
        <div className={styles.legendItem}>
          <span className={`${styles.legendDot} ${styles.legendAvailable}`} />
          <span>{t('available')}</span>
        </div>
        <div className={styles.legendItem}>
          <span className={`${styles.legendDot} ${styles.legendBlocked}`} />
          <span>{t('notAvailable')}</span>
        </div>
        <div className={styles.legendItem}>
          <span className={`${styles.legendDot} ${styles.legendUnavailable}`} />
          <span>{t('pastDate')}</span>
        </div>
      </div>
    </div>
  )
}

// Icons
function ChevronLeftIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )
}

function ChevronRightIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )
}
